import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons";
import { map, catchError } from "rxjs/operators";
import { firstValueFrom, of } from "rxjs";
import { FormlyFieldConfig } from "@ngx-formly/core";

import {
  SearchContainerComponent,
  SearchDataSource,
  SearchParams,
  SearchResult,
  SearchState,
  applyClientFilters,
  paginateItems,
} from "src/app/shared/components/search";

// Services and models
import { CallForOpportunityService } from "src/app/api/services/call-for-opportunity.service";
import { CallForOpportunity } from "src/app/api/models/call-for-opportunity";
import { CallForOpportunityPublish } from "src/app/api/models/call-for-opportunity-publish";
import { sortAnnouncementsNewestFirst } from "src/app/shared/utils/call-for-opportunity.utils";
import { AuthenticationService } from "src/app/shared/services/authentication.service";
import { FilterOptionsService } from "src/app/shared/services/filter-options.service";
import { RoleTypes } from "src/app/guards/roletypes";

// Components
import { CallForOpportunityCardComponent } from "./call-for-opportunity-card.component";

@Component({
  selector: "app-call-for-opportunities",
  providers: [SearchState],
  imports: [
    TranslateModule,
    SearchContainerComponent,
    FontAwesomeModule,
    CallForOpportunityCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-search-container
      [config]="searchConfig()"
      [dataSource]="dataSource"
      [fields]="fields()"
      [trackByFn]="trackByIndex"
      [showMatchFilter]="false"
    >
      <ng-template #headerTemplate>
        <div class="border-bottom mb-4 pb-3">
          <div class="d-flex align-items-center">
            <fa-icon
              [icon]="faBullhorn"
              class="text-primary mr-3"
              style="font-size: 2rem;"
            ></fa-icon>
            <div>
              <h1 class="mb-1">{{ "Announcements" | translate }}</h1>
              <p class="text-muted mb-0">
                {{
                  "Browse student announcements looking for opportunities"
                    | translate
                }}
              </p>
            </div>
          </div>
        </div>
      </ng-template>

      <ng-template #itemTemplate let-opportunity>
        <app-call-for-opportunity-card
          [data]="opportunity"
        ></app-call-for-opportunity-card>
      </ng-template>
    </app-search-container>
  `,
})
export class CallForOpportunitiesComponent {
  // Icons
  faBullhorn = faBullhorn;

  // Services
  private callForOpportunityService = inject(CallForOpportunityService);
  private authService = inject(AuthenticationService);
  private filterOptionsService = inject(FilterOptionsService);
  private searchState = inject(SearchState);

  // Role detection - determines which API to use
  private isCompanyUser = toSignal(
    this.authService.profile$.pipe(
      map((profile) => profile?.roles?.includes(RoleTypes.Company) ?? false)
    ),
    { initialValue: false }
  );

  // Formly fields configuration using shared field builders
  fields = computed<FormlyFieldConfig[]>(() => [
    this.filterOptionsService.searchTermField(),
    this.filterOptionsService.educationSearchTermField(),
    this.filterOptionsService.lookingForField(),
  ]);

  // Search config
  searchConfig = computed(() => ({
    searchOptions: this.searchState.searchOptions(),
    context: {
      queryParamOptions: ["SearchTerm", "EducationSearchTerm", "LookingFor"],
    },
    filterDisplayNames: {
      SearchTerm: "Search",
      EducationSearchTerm: "Education",
    },
    // Provide filter options for chip label lookups
    filterOptions: {
      LookingFor: this.filterOptionsService.lookingForOptions(),
    },
  }));

  // Data source - uses company or public API based on user role
  dataSource: SearchDataSource<CallForOpportunity | CallForOpportunityPublish> =
    {
      fetch: async (
        params: SearchParams
      ): Promise<
        SearchResult<CallForOpportunity | CallForOpportunityPublish>
      > => {
        try {
          const isCompany = this.isCompanyUser();

          // Select search fields based on user role
          const searchFields = isCompany
            ? ["studentFullName", "personalDescription", "id"]
            : ["firstName", "personalDescription"];

          // Fetch data based on user role
          let items: (CallForOpportunity | CallForOpportunityPublish)[];
          if (isCompany) {
            items = await firstValueFrom(
              this.callForOpportunityService.getCallForOpportunities().pipe(
                // Endpoint returns a paged wrapper { data, totalItems, ... }.
                map((res) => res.data ?? []),
                catchError(() => of([] as CallForOpportunity[]))
              )
            );
          } else {
            items = await firstValueFrom(
              this.callForOpportunityService
                .getPublicCallForOpportunities()
                .pipe(catchError(() => of([] as CallForOpportunityPublish[])))
            );
          }

          // Apply client-side filtering using utility
          // Type assertion needed due to union type
          const filtered = applyClientFilters(
            items as (CallForOpportunity | CallForOpportunityPublish)[],
            params,
            {
              searchFields: searchFields as (keyof (
                | CallForOpportunity
                | CallForOpportunityPublish
              ))[],
              educationFields: ["highestEducation"] as (keyof (
                | CallForOpportunity
                | CallForOpportunityPublish
              ))[],
              lookingForField: "lookingFor" as keyof (
                | CallForOpportunity
                | CallForOpportunityPublish
              ),
            }
          );

          // Newest announcements first, before paginating
          const sorted = sortAnnouncementsNewestFirst(filtered);

          // Apply pagination
          const { data, totalItems } = paginateItems(
            sorted,
            params.pageNumber || 1,
            params.pageSize || 12
          );

          return {
            data,
            totalItems,
            pageNumber: params.pageNumber || 1,
            pageSize: params.pageSize || 12,
          };
        } catch (error) {
          console.error("Error fetching call for opportunities:", error);
          return {
            data: [],
            totalItems: 0,
            pageNumber: params.pageNumber || 1,
            pageSize: params.pageSize || 12,
          };
        }
      },
    };

  trackByIndex(index: number): number {
    return index;
  }
}
